/**
 * THE PAGE'S HALF OF THE SERVICE WORKER — registering it, and telling it the two things it cannot
 * know on its own: where content lives, and which files make up the current build.
 *
 * A worker that will not register must never break the page, so every failure here is swallowed to
 * `undefined`: the app works without offline support, it simply is not offline-capable. The messages
 * are typed against the same `SwMessage` union the worker validates, so the two ends cannot drift.
 */
import { MSG_CONFIG, MSG_PREFETCH, PREFETCH_PROGRESS } from './strategy';
import type { PrefetchProgress } from './strategy';
import type { ConfigMessage, PrefetchMessage } from './strategy';

/** As much of the platform as this module reaches — narrowed so a test drives it without a browser. */
interface ServiceWorkerCapableNavigator {
  serviceWorker?: {
    register(url: string, options?: { type?: 'classic' | 'module' }): Promise<ServiceWorkerRegistration>;
  };
}

/** Anything that can receive a worker message — the active worker, or a fake in a test. */
export interface MessageTarget {
  postMessage(message: unknown, transfer?: Transferable[]): void;
}

/** What a download left behind: how many of the build's files reached the cache. */
export interface PrefetchResult {
  saved: number;
  total: number;
}

/**
 * Register the worker, once, where the platform has one. The worker is built as a classic script
 * (`../sw.ts` → `dist/sw.js`, IIFE), so it registers as `classic`; a browser without service workers,
 * or one that refuses the registration, simply gets no offline support and no error on screen.
 */
export async function registerServiceWorker(
  nav: ServiceWorkerCapableNavigator = navigator,
  url = '/sw.js',
): Promise<ServiceWorkerRegistration | undefined> {
  if (!nav.serviceWorker) return undefined;
  try {
    return await nav.serviceWorker.register(url, { type: 'classic' });
  } catch {
    return undefined;
  }
}

/** Tell the worker where content and the manifest live, so its cache decisions are exact. */
export function configureWorker(
  target: MessageTarget | null | undefined,
  contentBase: string,
  manifestUrl: string,
  /* Where the compilation is answered from, and the build being read (D26). Optional together: a
     base with no version names no cache partition, and the worker ignores either alone. */
  compilation?: { base: string; version: string },
): void {
  if (!target) return;
  const message: ConfigMessage = {
    type: MSG_CONFIG,
    contentBase,
    manifestUrl,
    ...(compilation ? { compilationBase: compilation.base, version: compilation.version } : {}),
  };
  target.postMessage(message);
}

/**
 * Ask the worker to pull the whole current build into its content cache, and resolve with what it
 * managed to save. The reply comes back down a private `MessageChannel` port, so a caller learns
 * when the download is actually done rather than only that it was asked for; with no worker to ask,
 * it resolves as though nothing was saved.
 */
export function requestOfflineDownload(
  target: MessageTarget | null | undefined,
  files: string[],
  /**
   * Told as each file is attempted, so a wait of minutes reads as progress rather than as a control
   * that has hung. Optional: a caller that only wants the outcome passes nothing and sees no change.
   */
  onProgress?: (done: number, total: number) => void,
): Promise<PrefetchResult> {
  if (!target) return Promise.resolve({ saved: 0, total: files.length });
  return new Promise<PrefetchResult>((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event: MessageEvent) => {
      /*
       * TWO KINDS DOWN ONE PORT, and the progress ones must not resolve it. The worker posts a
       * running count as it goes and exactly one reply at the end; treating any message as the end
       * would resolve on the first file and report the download finished before it had begun.
       */
      const data: unknown = event.data;
      if (typeof data === 'object' && data !== null && (data as { type?: unknown }).type === PREFETCH_PROGRESS) {
        const step = data as PrefetchProgress;
        if (typeof step.done === 'number' && typeof step.total === 'number') onProgress?.(step.done, step.total);
        return;
      }
      resolve(asPrefetchResult(data, files.length));
    };
    const message: PrefetchMessage = { type: MSG_PREFETCH, files };
    target.postMessage(message, [channel.port2]);
  });
}

/** The worker's reply is validated into shape, never trusted by its label alone. */
function asPrefetchResult(data: unknown, total: number): PrefetchResult {
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>;
    if (typeof record.saved === 'number' && typeof record.total === 'number') {
      return { saved: record.saved, total: record.total };
    }
  }
  return { saved: 0, total };
}

